import { isOperatorPermission, isTenantPermission } from "../permissions.js";
/**
 * Route marks from docs/05-api-contracts.md section 4. `step-up` needs a fresh step-up with an authenticator app;
 * `step-up-any` also takes one with a code sent by email or SMS, for a person without an authenticator (card 086).
 */
export const MARKS = ["tenant", "verified", "step-up", "step-up-any", "idem", "if-match"];
const PATH_PARAM = /\{([a-zA-Z]+)\}/g;
function fail(route, message) {
    throw new Error(`Route ${route.id} (${route.method} ${route.path}): ${message}`);
}
/**
 * Declares a route. The definition is checked when the module loads, so a route with a missing
 * permission, inconsistent path parameters, or contradictory marks never reaches runtime.
 */
export function defineRoute(route) {
    const prefixes = {
        tenant: "/v1/",
        public: "/v1/public/",
        operator: "/v1/ops/",
        webhook: "/webhooks/",
        internal: "/internal/",
    };
    if (!route.path.startsWith(prefixes[route.surface]))
        fail(route, `path must start with ${prefixes[route.surface]}`);
    if (route.surface === "tenant" &&
        (route.path.startsWith("/v1/public/") || route.path.startsWith("/v1/ops/"))) {
        fail(route, "tenant routes cannot live under /v1/public or /v1/ops");
    }
    const pathParams = [...route.path.matchAll(PATH_PARAM)].map((match) => match[1] ?? "").sort();
    const declared = Object.keys(route.request?.params?.shape ?? {}).sort();
    if (pathParams.join(",") !== declared.join(",")) {
        fail(route, `path parameters [${pathParams.join(", ")}] do not match params schema [${declared.join(", ")}]`);
    }
    if (new Set(route.marks).size !== route.marks.length)
        fail(route, "duplicate marks");
    if (route.permission !== "none") {
        const valid = route.surface === "operator"
            ? isOperatorPermission(route.permission)
            : isTenantPermission(route.permission);
        if (!valid)
            fail(route, `permission ${route.permission} does not belong to the ${route.surface} surface`);
    }
    const expectedAuth = {
        public: "none",
        operator: "operator",
        webhook: "signature",
        internal: "signature",
    };
    const required = expectedAuth[route.surface];
    if (required !== undefined &&
        route.auth !== required &&
        !(route.surface === "operator" && route.auth === "none")) {
        fail(route, `the ${route.surface} surface needs auth "${required}"`);
    }
    if (route.permission !== "none" && route.auth !== "user" && route.auth !== "operator") {
        fail(route, "a permission needs an authenticated caller");
    }
    const needsUser = ["tenant", "verified", "step-up", "step-up-any"];
    if (route.marks.some((mark) => needsUser.includes(mark)) && route.auth === "none") {
        fail(route, "tenant, verified, and step-up marks need an authenticated caller");
    }
    if (route.marks.includes("step-up") && route.marks.includes("step-up-any"))
        fail(route, "step-up and step-up-any exclude each other");
    if (route.marks.includes("verified") && !route.marks.includes("tenant"))
        fail(route, "verified implies tenant");
    if (route.method === "GET" && (route.marks.includes("idem") || route.request?.body !== undefined)) {
        fail(route, "GET routes take no body and no idempotency key");
    }
    if (Object.keys(route.responses).length === 0)
        fail(route, "at least one response is required");
    return Object.freeze(route);
}
//# sourceMappingURL=route.js.map